import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import UserAvatar from "../common/UserAvatar";

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function isOlderThan24h(dateStr) {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() > 86400000;
}

const MetricCell = ({ value, total, color, tooltipLabel }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Tooltip title={total > 0 ? `${pct}% ${tooltipLabel || ""}` : ""} arrow>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "2px", justifyContent: "center" }}>
        <Typography component="span" sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color }}>
          {value}
        </Typography>
        {total > 0 && (
          <Typography component="span" sx={{ fontSize: 10, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)" }}>
            /{total}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const COLUMNS = [
  { label: "Internal user", flex: 2 },
  { label: "Candidates", flex: 1 },
  { label: "Applications", flex: 1 },
  { label: "Reached", flex: 1 },
  { label: "Missed", flex: 1 },
  { label: "Sign ups", flex: 1 },
  { label: "Callback", flex: 1 },
  { label: "Applied", flex: 1 },
  { label: "Sign rate", flex: 1 },
  { label: "App rate", flex: 1 },
  { label: "Last activity", flex: 1.2 },
];

const InternalUsersTable = ({
  rows,
  onUserClick,
  showRank = false,
  currentUserId = null,
  maxVisible = 8,
}) => {
  const [showAll, setShowAll] = useState(false);
  const visibleRows = useMemo(
    () => (showAll ? rows : rows.slice(0, maxVisible)),
    [rows, showAll, maxVisible],
  );

  return (
    <Box sx={{ backgroundColor: "#ffffff", border: "1px solid hsl(140, 8%, 88%)", borderRadius: "8px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", px: "16px", py: "10px", borderBottom: "1px solid hsl(140, 8%, 88%)", alignItems: "center" }}>
        {showRank && (
          <Typography
            sx={{
              width: 40,
              flexShrink: 0,
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150, 10%, 46%)",
              textAlign: "center",
            }}
          >
            #
          </Typography>
        )}
        {COLUMNS.map((col) => (
          <Typography
            key={col.label}
            sx={{
              flex: col.flex,
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150, 10%, 46%)",
              textAlign: col.label === "Internal user" ? "left" : "center",
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>

      {visibleRows.map((u, idx) => {
        const inactive = u.reached_total === 0 && !u.candidates_created && !u.applications_submitted;
        const isMe = currentUserId != null && u.internal_user.id === currentUserId;
        const rank = u.rank != null ? u.rank : idx + 1;
        const signRate = u.sign_rate ?? 0;
        const appRate = u.applied_rate ?? 0;
        const lastAct = relativeTime(u.last_activity_at);

        let lastActColor = "hsl(150, 10%, 46%)";
        if (!u.last_activity_at) lastActColor = "hsl(0, 72%, 51%)";
        else if (isOlderThan24h(u.last_activity_at)) lastActColor = "hsl(38, 92%, 50%)";

        let borderLeft = "3px solid transparent";
        if (isMe) borderLeft = "3px solid hsl(150, 38%, 17%)";
        else if (inactive) borderLeft = "3px solid hsl(0, 72%, 51%)";

        return (
          <Box
            key={u.internal_user.id}
            onClick={() => onUserClick?.(u.internal_user.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: "16px",
              py: "14px",
              borderBottom: "1px solid hsl(140, 8%, 88%)",
              cursor: "pointer",
              transition: "background-color 150ms ease",
              borderLeft,
              backgroundColor: isMe ? "hsl(150, 40%, 97%)" : "transparent",
              "&:hover": { backgroundColor: isMe ? "hsl(150, 40%, 95%)" : "hsl(140, 8%, 90%)" },
            }}
          >
            {showRank && (
              <Box sx={{ width: 40, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    fontVariantNumeric: "tabular-nums",
                    color: rank <= 3 ? "hsl(40, 68%, 42%)" : "hsl(150, 10%, 46%)",
                  }}
                >
                  {rank}
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 2, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <UserAvatar user={u.internal_user} size={28} />
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)" }} noWrap>
                {u.internal_user.full_name}
              </Typography>
              {isMe && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150, 38%, 17%)",
                    backgroundColor: "hsl(80, 60%, 88%)",
                    px: "6px",
                    py: "1px",
                    borderRadius: "5px",
                    letterSpacing: "0.03em",
                    flexShrink: 0,
                  }}
                >
                  YOU
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(174, 60%, 38%)" }}>
                {u.candidates_created ?? 0}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(262, 65%, 55%)" }}>
                {u.applications_submitted ?? 0}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <MetricCell value={u.reached} total={u.reached_total} color="hsl(142, 71%, 45%)" tooltipLabel="reached" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <MetricCell value={u.missed} total={u.reached_total} color="hsl(0, 72%, 51%)" tooltipLabel="missed" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <MetricCell value={u.interested} total={u.reached} color="hsl(38, 92%, 50%)" tooltipLabel="sign ups" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <MetricCell value={u.callback} total={u.reached} color="hsl(217, 91%, 55%)" tooltipLabel="callback" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <MetricCell value={u.applied} total={u.reached} color="hsl(150, 38%, 17%)" tooltipLabel="applied" />
            </Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: signRate > 0 ? "hsl(142, 71%, 45%)" : "hsl(150, 10%, 46%)" }}>
                {signRate}%
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: appRate > 0 ? "hsl(142, 71%, 45%)" : "hsl(150, 10%, 46%)" }}>
                {appRate}%
              </Typography>
            </Box>
            <Box sx={{ flex: 1.2, textAlign: "center" }}>
              <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: lastActColor }}>
                {lastAct || "Not started"}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {rows.length > maxVisible && (
        <Box
          component="button"
          onClick={() => setShowAll((v) => !v)}
          sx={{
            all: "unset",
            width: "100%",
            textAlign: "center",
            py: "10px",
            fontSize: 11,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150, 10%, 46%)",
            cursor: "pointer",
            borderTop: "1px solid hsl(140, 8%, 88%)",
            transition: "color 150ms ease",
            "&:hover": { color: "hsl(150, 30%, 12%)" },
          }}
        >
          {showAll ? "Show less" : `Show all ${rows.length} internal users`}
        </Box>
      )}
    </Box>
  );
};

export default InternalUsersTable;
